/**
 * Текст выпуска «Что нового» для канала в Telegram. Отдельный модуль без зависимостей:
 * его проверяет scripts/releases.test.mjs.
 */

/** Заголовок и пункты — то же, что ReleaseInput, без зависимости от API. */
export interface TelegramRelease {
  title: string
  items: { text: string }[]
}

/** Код внутри обратных кавычек: его Telegram показывает как есть, и мы его тоже не трогаем. */
const CODE = /(```[\s\S]*?```|`[^`\n]+`)/

/**
 * Разметка чата → разметка, которую Telegram понимает при отправке: **жирный**, __курсив__,
 * ~~зачёркнутый~~, ||спойлер||, `код`. Подчёркивания в Telegram нет — там __ это курсив,
 * поэтому подчёркнутый текст уходит без разметки. Ссылка с текстом — «текст (адрес)».
 * Код остаётся как написан: `__init__` в обратных кавычках не теряет подчёркиваний.
 */
export function telegramMarkdown(text: string): string {
  return text
    .split(CODE)
    .map((part, i) => (i % 2 === 1 ? part : convert(part)))
    .join('')
}

function convert(text: string): string {
  return text
    .replace(/<a?:([A-Za-z0-9_]{2,32}):\d+>/g, ':$1:')
    .replace(/<@[!&]?\d+>/g, '')
    .replace(/<#\d+>/g, '#канал')
    .replace(/\[([^[\]\n]+)\]\(<?(https?:\/\/[^\s)>]+)>?\)/g, '$1 ($2)')
    .replace(/<(https?:\/\/[^\s<>]+)>/g, '$1')
    .replace(/__([\s\S]+?)__(?!_)/g, '$1')
    .replace(/\*\*\*([^*]+)\*\*\*/g, '**__$1__**')
    .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1__$2__')
    .replace(/(^|[\s(])_([^_\n]+)_(?=$|[\s.,!?:;)])/gm, '$1__$2__')
    .replace(/^#{1,3} (.+)$/gm, '**$1**')
    .replace(/^-# /gm, '')
}

/*
 * Подзаголовок пункта — как SplitLead на сервере (backend/internal/releases): жирное начало,
 * за которым точка, двоеточие, тире через пробелы или перенос строки: «**Статусы.** Нажмите…».
 * Жирное слово в предложении («**RNNoise** теперь включён») подзаголовком не считается.
 */
const LEAD = /^\*\*([^*\n]+?)\*\*(?:([ \t]*[.:][ \t]*|[ \t]+[—–-][ \t]+|[ \t]*\n)|[ \t]*)/

export function splitLead(text: string): { lead: string; rest: string } | null {
  const m = LEAD.exec(text)
  if (!m) return null
  const bold = m[1]!.trim()
  if (!bold || (m[2] === undefined && !/[.:!?]$/.test(bold))) return null
  const lead = bold.replace(/[.:]+$/, '')
  const rest = text.slice(m[0].length).trim()
  return lead && rest ? { lead, rest } : null
}

/**
 * Текст поста для канала в Telegram — как в #обновления: заголовок жирным, пункты через пустую
 * строку, жирное начало пункта — отдельной строкой над абзацем.
 */
export function telegramText(input: TelegramRelease): string {
  const blocks = [`**${input.title.trim().replaceAll('*', '')}**`]
  for (const it of input.items) {
    const text = it.text.trim()
    if (!text) continue
    const parts = splitLead(text)
    blocks.push(parts ? `**${telegramMarkdown(parts.lead)}**\n${telegramMarkdown(parts.rest)}` : `• ${telegramMarkdown(text)}`)
  }
  return blocks.join('\n\n')
}

const escapeHtml = (s: string) => s.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')

/** То же в HTML: так формат сохраняется при вставке туда, где разметку при отправке не разбирают. */
export function telegramHtml(input: TelegramRelease): string {
  const inline = (s: string) =>
    s
      .split(/(`[^`\n]+`)/)
      .map((part, i) =>
        i % 2 === 1
          ? `<code>${escapeHtml(part.slice(1, -1))}</code>`
          : escapeHtml(part)
              .replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>')
              .replace(/__([^_]+)__/g, '<i>$1</i>')
              .replace(/~~([^~]+)~~/g, '<s>$1</s>'),
      )
      .join('')
  return telegramText(input).split('\n').map(inline).join('<br>')
}
